import StorageEssentialsApi from "@glidevvr/se-api-wrapper";
import { createApi } from "@reduxjs/toolkit/query/react";
import type { RootState } from "@/store";
import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import type { UnitGroup, Facility } from "@/types";

// Define a consistent error type
export interface ApiError {
    status: number;
    message: string;
}

export interface GetUnitGroupsParams {
    "facility-ids": string;
    group?: string;
    sort?: string;
    "unit-source"?: string;
    width?: number;
    "width-gt"?: number;
    "width-lt"?: number;
    length?: number;
    "length-gt"?: number;
    "length-lt"?: number;
    categories?: string;
    features?: string;
    strict?: boolean;
    "min-price"?: boolean;
    "unit-id"?: number;
}

export interface ResponseMeta {
    execution_time: number;
    count: number;
}

type GetUnitGroupsResponse = { data: UnitGroup[]; meta: ResponseMeta };

export interface GetFacilitiesParams {
    location?: string;
    radius?: number;
}

type GetFacilitiesResponse = { data: Facility[]; meta: ResponseMeta };

export interface GetFacilitiesV2Params {
    ids?: string;
    states?: string;
    cities?: string;
    zipcodes?: string;
    source?: string;
    lat?: number;
    lng?: number;
    distance?: number;
    unit?: "mi" | "km";
    withSettings?: boolean;
}

export type GetFacilitiesV2Response = { data: Facility[]; meta: ResponseMeta };

// Define a discriminated union for type-safe method calls
type ApiMethod =
    | { method: "getUnitGroups"; params: GetUnitGroupsParams }
    | { method: "getFacilities"; params: GetFacilitiesParams }
    | { method: "getFacilitiesV2"; params: GetFacilitiesV2Params };

const seApiBaseQuery: BaseQueryFn<ApiMethod, unknown, ApiError> = async (
    { method, params },
    api,
) => {
    try {
        const state = api.getState() as RootState;
        const apiKey = state.apiKey.apiKey;

        if (!apiKey) {
            return {
                error: {
                    status: 401,
                    message: "API key is not available",
                },
            };
        }

        const environment = import.meta.env.VITE_SE_ENVIRONMENT;
        const seApi = new StorageEssentialsApi(apiKey, environment);

        // Type-safe method calls with switch statement
        let response;
        switch (method) {
            case "getUnitGroups": {
                response = await seApi.getUnitGroups(params);
                break;
            }
            case "getFacilities": {
                response = await seApi.getFacilities(params);
                break;
            }
            case "getFacilitiesV2": {
                response = await seApi.getFacilitiesV2(params);
                break;
            }
            default: {
                /*
                 * TS exhaustiveness checking
                 * This block should never be reached if all cases are handled above.
                 * If a new method is added to the ApiMethod union type but not handled here,
                 * TypeScript will raise a compile-time error. Poggers.
                 */

                // TypeScript has eliminated all possibilities, so method has the type never (meaning "this should never happen")
                const _exhaustive: never = method;

                // Tels ESLint that the unused variable is intentional and only exists for compile-time type checking.
                void _exhaustive;
                return {
                    error: {
                        status: 500,
                        message: "Unknown method",
                    },
                };
            }
        }

        return { data: response.data };
    } catch (error) {
        // Extract status code if available from the API wrapper error
        let status = 500; // default to 500 for unknown errors
        let message = "Unknown error occurred";

        if (error instanceof Error) {
            message = error.message;
            // Check if the error object has a status property (common in API wrappers)
            if ("status" in error && typeof error.status === "number") {
                status = error.status;
            } else if (
                "statusCode" in error &&
                typeof error.statusCode === "number"
            ) {
                status = error.statusCode;
            }
        }

        const apiError: ApiError = { status, message };
        console.error("API Error: ", apiError);
        return { error: apiError };
    }
};

// Define your API slice
export const seApi = createApi({
    reducerPath: "seApi",
    baseQuery: seApiBaseQuery,
    endpoints: (builder) => ({
        getUnitGroups: builder.query<
            GetUnitGroupsResponse,
            GetUnitGroupsParams
        >({
            query: (params) => ({
                method: "getUnitGroups",
                params,
            }),
        }),
        getFacilities: builder.query<
            GetFacilitiesResponse,
            GetFacilitiesParams
        >({
            query: (params) => ({
                method: "getFacilities",
                params,
            }),
        }),
        getFacilitiesV2: builder.query<
            GetFacilitiesV2Response,
            GetFacilitiesV2Params
        >({
            query: (params) => ({
                method: "getFacilitiesV2",
                params,
            }),
        }),
    }),
});

// Export hooks for your components to use
export const {
    useGetUnitGroupsQuery,
    useGetFacilitiesQuery,
    useGetFacilitiesV2Query,
} = seApi;
